/**
 * Intern overzicht: welke API-routes de app gebruikt (bouwsteen API-dekking)
 */
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';

const ENDPOINTS = [
  { method: 'POST', path: '/resolve_input', note: 'Intent uit tekst/tekening-URL' },
  {
    method: 'POST',
    path: '/analyze_documents',
    note: 'Tekening/PDF-analyse → voorstel (library); zelfde scan voor alle klanttypes; import_tier per regel; optioneel max_tokens',
  },
  { method: 'POST', path: '/upload_document', note: 'Bestand uploaden' },
  { method: 'POST', path: '/assistant/public/threads', note: 'Publieke landing-assistent — thread (geen auth, rate limit IP)' },
  { method: 'POST', path: '/assistant/public/threads/:id/messages', note: 'Publiek bericht (geen tools)' },
  { method: 'GET', path: '/assistant/public/threads/:id', note: 'Publieke thread ophalen (zelfde IP als aanmaak)' },
  { method: 'GET', path: '/assistant/threads', note: 'Assistent-threads (ingelogd)' },
  { method: 'POST', path: '/assistant/threads', note: 'Thread aanmaken' },
  { method: 'POST', path: '/assistant/threads/:id/messages', note: 'Bericht' },
  { method: 'GET', path: '/assistant/threads/:id', note: 'Thread ophalen' },
  { method: 'POST', path: '/calculate_system', note: 'Enkel systeem berekenen' },
  {
    method: 'POST',
    path: '/aggregate_project',
    note: 'Project totalen; optioneel labor_overrides — standaard uit tenant labor-rates (D1)',
  },
  { method: 'POST', path: '/generate_document', note: 'Offerte / document JSON' },
  { method: 'POST', path: '/generate_document_pdf', note: 'Zelfde body als generate_document → PDF' },
  {
    method: 'POST',
    path: '/export_project',
    note: 'Begroting: CSV/Excel/pdf — optioneel include_cost_summary, include_regel_detail, include_co2, include_export_disclaimer, include_per_deel_begroting, project_label',
  },
  { method: 'GET', path: '/library/groups', note: 'Hoofdgroepen (2-cijferige taxonomie)' },
  { method: 'GET', path: '/templates', note: 'Legacy templates JSON (optioneel)' },
  {
    method: 'GET',
    path: '/systems',
    note: 'Prijsboek: projectcontext + narrowing (group_code | discipline | q≥2); optioneel catalog_subgroup (met group_code); max 50',
  },
  { method: 'GET', path: '/library/subgroups', note: 'Subgroepen per group_code (prijsboek)' },
  { method: 'GET', path: '/catalog/disciplines', note: 'Disciplines voor filter-UI' },
  { method: 'GET', path: '/catalog/meta', note: 'Catalogusversie + aantallen (geen projectcontext)' },
  {
    method: 'GET',
    path: '/catalog/materials',
    note: 'Referentiematerialen (q, limit, offset) — transparantie prijsbibliotheek',
  },
  { method: 'GET', path: '/systems/:id/variants', note: 'Varianten (merge seed + D1 catalog_variants)' },
  {
    method: 'POST',
    path: '/catalog/import',
    note: 'Bulk-import + QA-validatie (strict_validation optioneel; zie docs/CATALOG_IMPORT.md)',
  },
  { method: 'GET', path: '/regions', note: 'Regio’s' },
  { method: 'GET', path: '/index_years', note: 'Indexjaren' },
  { method: 'GET', path: '/project_types', note: 'Projecttypes' },
  { method: 'GET', path: '/building_types', note: 'Gebouwtypes' },
  { method: 'GET', path: '/projects', note: 'Lijst projecten' },
  { method: 'POST', path: '/projects', note: 'Aanmaken (incl. tekening-voorstel)' },
  { method: 'GET', path: '/projects/:id', note: 'Project + context + drawing_proposal' },
    { method: 'PUT', path: '/projects/:id', note: 'Update, systems, context, approve_drawing_proposal + approve_drawing_proposal_mode (replace|append|merge)' },
  { method: 'DELETE', path: '/projects/:id', note: 'Verwijderen' },
  { method: 'POST', path: '/submit_feedback', note: 'Feedback' },
  { method: 'POST', path: '/validate_payload', note: 'Validatie' },
  { method: 'POST', path: '/woz_support', note: 'WOZ' },
  { method: 'POST', path: '/calculate_subsidies', note: 'Subsidies' },
  { method: 'POST', path: '/compare_scenarios', note: 'Scenario’s' },
  { method: 'GET/PUT', path: '/tenants/:id/overrides', note: 'Tenant overrides' },
  {
    method: 'GET/PUT',
    path: '/tenants/:id/labor-rates',
    note: 'Uurtarieven tenant (GET alle ingelogde users; PUT accountbeheer) — aggregate/export/offerte gebruiken DB + optioneel labor_overrides body',
  },
  { method: 'GET/PUT', path: '/tenants/:id/billing', note: 'Handmatige facturatie — alleen superadmin (Platformbeheer); tenant-checkout via Stripe op /billing/*' },
  { method: 'GET/POST', path: '/maintenance_plans', note: 'MJOP' },
  { method: 'GET/POST', path: '/portfolios', note: 'Portfolio' },
  {
    method: 'POST',
    path: '/lead',
    note: 'Marketing lead (publiek; honeypot; rate limit KV; optioneel Cloudflare Turnstile) — SEO + /api',
  },
  { method: 'GET', path: '/public/cms-reviews', note: 'Publieke reviews-pagina payload (KV of ingebouwde voorbeelden)' },
  { method: 'GET/PUT', path: '/admin/cms-reviews', note: 'Reviews-CMS — alleen superadmin; PUT = volledige payload schema_version 1' },
  { method: 'POST', path: '/analytics/event', note: 'Analytics event' },
  {
    method: 'GET',
    path: '/analytics/stats',
    note: 'Statistieken (+ signup/regio/typologie); ?format=csv&delimiter=; — platformbeheerder',
  },
  {
    method: 'GET',
    path: '/admin/seo-health',
    note: 'SEO health (superadmin): first-party traffic + robots/sitemap/URL-probes; ?days=&site_origin=&max_url_checks=',
  },
  {
    method: 'GET',
    path: '/admin/library-coverage',
    note: 'Dekking per hoofdgroep 10–71 (live merge); catalog_released_at + histogram + systemen/varianten — platformbeheerder',
  },
  { method: 'GET', path: '/reference_costs', note: 'Legacy template-benchmark (niet in app-UI)' },
  { method: 'GET', path: '/region_suggest?postal_code=', note: 'Regio-voorstel op basis van NL-postcode (heuristiek)' },
  { method: 'GET', path: '/auth/me', note: 'Sessie / tenant + tenant_billing (proef/licentie)' },
] as const;

export function ApiTools() {
  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/admin/api-tools"
        noindex
        title="API-overzicht"
        description="Intern overzicht van CalqTool™ API-routes voor platformbeheer — endpoints, methodes en korte toelichting. Niet bedoeld voor openbare indexering."
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs
          items={[
            { label: 'Platformbeheer', href: '/admin' },
            { label: 'API-overzicht' },
          ]}
        />
        <h1 className="mb-2 text-2xl font-bold text-text">API-overzicht</h1>
        <p className="mb-6 max-w-2xl text-sm text-text-muted">
          <strong>Alleen voor platformbeheerders.</strong> Dit overzicht hoort bij bouwsteen{' '}
          <strong>API- & tools-dekking</strong>. OpenAPI-specificatie staat in de worker onder{' '}
          <code className="rounded bg-surface px-1 text-xs">workers/api/docs/openapi.yaml</code>.
        </p>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-card">
                <th className="p-3 font-semibold text-text">Methode</th>
                <th className="p-3 font-semibold text-text">Pad</th>
                <th className="p-3 font-semibold text-text">Gebruik</th>
              </tr>
            </thead>
            <tbody>
              {ENDPOINTS.map((row) => (
                <tr key={`${row.method}-${row.path}`} className="border-b border-border/80">
                  <td className="p-3 font-mono text-xs text-text-muted">{row.method}</td>
                  <td className="p-3 font-mono text-xs text-primary">{row.path}</td>
                  <td className="p-3 text-text-muted">{row.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AnimatedSection>
    </div>
  );
}
